import { Credentials } from '@/types/auth.types';
import { ConnectionData, StartCallRequest, ConnectError, ConnectErrorType, ConnectErrorClass } from '@/types/connect.types';
import { configService } from './configService';
import { debug } from '@/config/constants';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@aws-sdk/protocol-http';

interface StartCallResponse {
  contactId: string;
  sessionId: string;
  connectionData: ConnectionData;
}

class ConnectService {
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_MS = 1000;

  /**
   * Start a WebRTC call to Amazon Connect
   * @param credentials - AWS IAM credentials for request signing
   * @param sessionId - Unique session identifier
   * @param featureName - Optional feature context
   * @param queueId - Optional queue ID if coming from queue
   * @returns Connection data for Chime SDK
   */
  async startWebRTCCall(
    credentials: Credentials,
    sessionId: string,
    featureName?: string,
    queueId?: string
  ): Promise<ConnectionData> {
    const config = configService.getConfig();
    
    if (!config) {
      throw this.createError(
        ConnectErrorType.INVALID_REQUEST,
        'Configuration not loaded',
        false
      );
    }

    const request: StartCallRequest = {
      sessionId,
      featureName,
      queueId,
    };

    try {
      const response = await this.makeSignedRequest(
        config.API_ENDPOINT,
        config.REGION,
        credentials,
        request
      );

      debug.log('✓ Call started successfully:', response.contactId);
      return response.connectionData;

    } catch (error) {
      console.error('✗ Failed to start call:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Make a signed HTTP request to API Gateway
   */
  private async makeSignedRequest(
    apiEndpoint: string,
    region: string,
    credentials: Credentials,
    body: any,
    retryCount = 0,
    endpoint: string = 'start-call'
  ): Promise<StartCallResponse> {
    try {
      // Parse API endpoint
      const url = new URL(`${apiEndpoint}${endpoint}`);
      
      // Create HTTP request
      const request = new HttpRequest({
        method: 'POST',
        protocol: url.protocol,
        hostname: url.hostname,
        path: url.pathname,
        headers: {
          'Content-Type': 'application/json',
          'host': url.hostname,
        },
        body: JSON.stringify(body),
      });

      // Sign request with IAM credentials
      const signer = new SignatureV4({
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
        region,
        service: 'execute-api',
        sha256: Sha256,
      });

      const signedRequest = await signer.sign(request);

      // Make HTTP request
      const response = await fetch(`${url.protocol}//${url.hostname}${url.pathname}`, {
        method: signedRequest.method,
        headers: signedRequest.headers as HeadersInit,
        body: signedRequest.body,
        credentials: 'include', // Required for CORS with credentials
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 429) {
          throw this.createError(
            ConnectErrorType.CAPACITY_EXCEEDED,
            responseData.error || 'Too many active calls',
            false
          );
        }

        if (response.status === 400) {
          throw this.createError(
            ConnectErrorType.INVALID_REQUEST,
            responseData.error || 'Invalid request',
            false
          );
        }

        throw new Error(responseData.error || `HTTP ${response.status}`);
      }

      return responseData as StartCallResponse;

    } catch (error) {
      // Retry on network errors
      if (this.isRetryableError(error) && retryCount < this.MAX_RETRIES) {
        debug.warn(`Retrying request (${retryCount + 1}/${this.MAX_RETRIES})...`);
        await this.delay(this.RETRY_DELAY_MS * Math.pow(2, retryCount));
        return this.makeSignedRequest(apiEndpoint, region, credentials, body, retryCount + 1, endpoint);
      }

      throw error;
    }
  }

  /**
   * Check if error is retryable
   */
  private isRetryableError(error: any): boolean {
    if (error instanceof ConnectErrorClass) {
      return error.retryable;
    }

    // Network errors are retryable
    if (error.name === 'TypeError' || error.message?.includes('fetch')) {
      return true;
    }

    // Timeout errors are retryable
    if (error.message?.includes('timeout')) {
      return true;
    }

    return false;
  }

  /**
   * Handle and transform errors
   */
  private handleError(error: any): ConnectError {
    if (error instanceof ConnectErrorClass) {
      return error;
    }

    // Network errors
    if (error.name === 'TypeError' || error.message?.includes('fetch')) {
      return this.createError(
        ConnectErrorType.NETWORK_ERROR,
        'Network connection failed. Please check your internet connection.',
        true
      );
    }

    // Generic Connect errors
    return this.createError(
      ConnectErrorType.CONNECT_ERROR,
      error.message || 'Failed to start call',
      false,
      error
    );
  }

  /**
   * Create a typed error
   */
  private createError(
    type: ConnectErrorType,
    message: string,
    retryable: boolean,
    originalError?: Error
  ): ConnectError {
    return new ConnectErrorClass(type, message, retryable, originalError);
  }

  /**
   * End a call and clean up the session
   * @param credentials - AWS IAM credentials for request signing
   * @param sessionId - Session ID to end
   */
  async endCall(
    credentials: Credentials,
    sessionId: string
  ): Promise<void> {
    const config = configService.getConfig();
    
    if (!config) {
      throw this.createError(
        ConnectErrorType.INVALID_REQUEST,
        'Configuration not loaded',
        false
      );
    }

    try {
      await this.makeSignedRequest(
        config.API_ENDPOINT,
        config.REGION,
        credentials,
        { sessionId },
        0,
        'end-call'
      );

      debug.log('✓ Session ended successfully');
    } catch (error) {
      console.error('✗ Failed to end session:', error);
      // Don't throw - session cleanup is best-effort
    }
  }

  /**
   * Delay helper for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const connectService = new ConnectService();
